import "./dropdown.css";

// Dropdown — single-selection picker with a raised trigger button and a
// recessed popover menu. Built from scratch (rather than skinning a native
// `<select>`) so the open menu can use the same neumorphic panel grammar
// as the rest of the synth components — the native popup is browser-
// controlled and always reads as foreign.
//
// Behavior:
//   - Click trigger: toggle popover.
//   - Click item: select + close.
//   - Click outside: close.
//   - Escape: close (returning focus to the trigger).
//   - ArrowDown on closed trigger: open + focus first item.
//   - ArrowUp/Down inside popover: move keyboard focus between items.
//   - Enter / Space on focused item: select + close.
//   - Tab: leaves the popover (closes it on the way out).

export interface DropdownOption<V extends string | number> {
  value: V;
  label: string;
  /** Optional sub-label rendered dimmer to the right of the main label
   *  (e.g. manufacturer name, port id). */
  hint?: string;
  disabled?: boolean;
}

export interface DropdownOptions<V extends string | number> {
  container: HTMLElement;
  options: DropdownOption<V>[];
  /** Currently-selected value. Defaults to the first option's value. */
  value?: V;
  /** Optional persistent label above the trigger. */
  label?: string;
  /** Placeholder shown when no option matches the current value. */
  placeholder?: string;
  /** Minimum trigger width in CSS px. */
  minWidth?: number;
  onChange?: (value: V) => void;
  /** Demo-only: open the popover on mount and skip outside-click close so
   *  the visual state can be screenshotted statically. */
  forceOpen?: boolean;
}

export class Dropdown<V extends string | number> {
  private root: HTMLDivElement;
  private trigger: HTMLButtonElement;
  private triggerLabelEl: HTMLSpanElement;
  private chevron: HTMLCanvasElement;
  private popover: HTMLDivElement;
  private itemEls: HTMLButtonElement[] = [];

  private opts: DropdownOption<V>[];
  private currentValue: V | undefined;
  private placeholder: string;
  private onChange: ((value: V) => void) | undefined;
  private forceOpen: boolean;

  private open = false;
  private focusedIndex = -1;
  private outsideClickHandler: ((ev: MouseEvent) => void) | null = null;

  constructor(opts: DropdownOptions<V>) {
    this.opts = opts.options;
    this.currentValue = opts.value ?? opts.options[0]?.value;
    this.placeholder = opts.placeholder ?? "select…";
    if (opts.onChange) this.onChange = opts.onChange;
    this.forceOpen = opts.forceOpen ?? false;

    this.root = document.createElement("div");
    this.root.className = "dropdown";

    if (opts.label) {
      const lbl = document.createElement("span");
      lbl.className = "label dropdown-label";
      lbl.textContent = opts.label;
      this.root.append(lbl);
    }

    this.trigger = document.createElement("button");
    this.trigger.type = "button";
    this.trigger.className = "dropdown-trigger";
    this.trigger.setAttribute("aria-haspopup", "listbox");
    this.trigger.setAttribute("aria-expanded", "false");
    if (opts.minWidth) this.trigger.style.minWidth = `${opts.minWidth}px`;

    this.triggerLabelEl = document.createElement("span");
    this.triggerLabelEl.className = "dropdown-trigger-label";
    this.trigger.append(this.triggerLabelEl);

    this.chevron = document.createElement("canvas");
    this.chevron.className = "dropdown-trigger-chevron";
    this.chevron.width = 12;
    this.chevron.height = 12;
    this.trigger.append(this.chevron);

    this.popover = document.createElement("div");
    this.popover.className = "dropdown-popover";
    this.popover.setAttribute("role", "listbox");
    this.popover.tabIndex = -1;

    this.root.append(this.trigger, this.popover);
    opts.container.append(this.root);

    this.rebuildItems();
    this.drawChevron();
    this.refreshTriggerLabel();
    this.attachInteraction();

    if (this.forceOpen) this.setOpen(true);
  }

  getValue(): V | undefined {
    return this.currentValue;
  }

  setValue(value: V): void {
    if (value === this.currentValue) return;
    this.currentValue = value;
    this.refreshTriggerLabel();
    this.refreshSelectedItem();
    // Fire onChange on every actual change (programmatic or otherwise)
    // so consumers using the dropdown as the source of truth for an
    // engine param can rely on the callback firing for preset loads.
    this.onChange?.(value);
  }

  /** Replace the option list. Preserves the current selection if it still
   *  exists; otherwise falls back to the first option (or `undefined` if
   *  the new list is empty). */
  setOptions(options: DropdownOption<V>[]): void {
    this.opts = options;
    const stillThere = options.some((o) => o.value === this.currentValue);
    if (!stillThere) this.currentValue = options[0]?.value;
    this.rebuildItems();
    this.refreshTriggerLabel();
  }

  destroy(): void {
    if (this.outsideClickHandler) {
      window.removeEventListener("mousedown", this.outsideClickHandler, true);
      this.outsideClickHandler = null;
    }
    this.root.remove();
  }

  // ----- DOM construction -----

  private rebuildItems(): void {
    this.popover.replaceChildren();
    this.itemEls = [];
    for (let i = 0; i < this.opts.length; i++) {
      const o = this.opts[i]!;
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "dropdown-item";
      btn.setAttribute("role", "option");
      btn.dataset.value = String(o.value);
      btn.dataset.index = String(i);
      if (o.disabled) btn.setAttribute("disabled", "true");

      const marker = document.createElement("span");
      marker.className = "dropdown-item-marker";
      btn.append(marker);

      const lbl = document.createElement("span");
      lbl.className = "dropdown-item-label";
      lbl.textContent = o.label;
      btn.append(lbl);

      if (o.hint) {
        const hint = document.createElement("span");
        hint.className = "dropdown-item-hint label";
        hint.textContent = o.hint;
        btn.append(hint);
      }

      btn.addEventListener("click", (ev) => {
        ev.preventDefault();
        if (o.disabled) return;
        this.select(o.value);
      });
      btn.addEventListener("mouseenter", () => {
        this.focusedIndex = i;
        this.updateFocusVisual();
      });

      this.popover.append(btn);
      this.itemEls.push(btn);
    }
    this.refreshSelectedItem();
  }

  // ----- selection state -----

  private select(value: V): void {
    const same = value === this.currentValue;
    if (!same) {
      this.currentValue = value;
      this.refreshTriggerLabel();
      this.refreshSelectedItem();
      this.onChange?.(value);
    }
    this.setOpen(false);
    this.trigger.focus();
  }

  private refreshTriggerLabel(): void {
    const match = this.opts.find((o) => o.value === this.currentValue);
    this.triggerLabelEl.textContent = match?.label ?? this.placeholder;
    this.triggerLabelEl.setAttribute(
      "aria-current",
      match ? "true" : "false",
    );
  }

  private refreshSelectedItem(): void {
    for (let i = 0; i < this.itemEls.length; i++) {
      const o = this.opts[i]!;
      const el = this.itemEls[i]!;
      const sel = o.value === this.currentValue;
      el.classList.toggle("is-selected", sel);
      el.setAttribute("aria-selected", sel ? "true" : "false");
    }
  }

  // ----- open / close -----

  private setOpen(open: boolean): void {
    if (open === this.open) return;
    this.open = open;
    this.root.classList.toggle("is-open", open);
    this.trigger.classList.toggle("is-open", open);
    this.trigger.setAttribute("aria-expanded", open ? "true" : "false");
    if (open) {
      // Start focus on the currently-selected item if there is one,
      // otherwise on the first non-disabled item.
      const selIdx = this.opts.findIndex((o) => o.value === this.currentValue);
      this.focusedIndex =
        selIdx >= 0 ? selIdx : this.firstEnabledIndex();
      this.updateFocusVisual();
      this.attachOutsideClick();
    } else {
      this.focusedIndex = -1;
      this.updateFocusVisual();
      this.detachOutsideClick();
    }
  }

  private firstEnabledIndex(): number {
    for (let i = 0; i < this.opts.length; i++) {
      if (!this.opts[i]!.disabled) return i;
    }
    return -1;
  }

  private updateFocusVisual(): void {
    for (let i = 0; i < this.itemEls.length; i++) {
      const el = this.itemEls[i]!;
      el.classList.toggle("is-focused", i === this.focusedIndex);
    }
  }

  private attachOutsideClick(): void {
    if (this.forceOpen) return;
    if (this.outsideClickHandler) return;
    this.outsideClickHandler = (ev: MouseEvent) => {
      if (this.root.contains(ev.target as Node)) return;
      this.setOpen(false);
    };
    // Capture phase so we beat any inner click handlers.
    window.addEventListener("mousedown", this.outsideClickHandler, true);
  }

  private detachOutsideClick(): void {
    if (!this.outsideClickHandler) return;
    window.removeEventListener("mousedown", this.outsideClickHandler, true);
    this.outsideClickHandler = null;
  }

  // ----- interaction -----

  private attachInteraction(): void {
    this.trigger.addEventListener("click", () => {
      this.setOpen(!this.open);
    });

    this.trigger.addEventListener("keydown", (ev) => {
      if (ev.key === "ArrowDown" || ev.key === "Enter" || ev.key === " ") {
        ev.preventDefault();
        if (!this.open) this.setOpen(true);
      } else if (ev.key === "ArrowUp" && !this.open) {
        ev.preventDefault();
        this.setOpen(true);
      } else if (ev.key === "Escape" && this.open) {
        ev.preventDefault();
        this.setOpen(false);
      }
    });

    // Keyboard nav while open lives on the popover so item focus can step
    // without bouncing focus through the trigger.
    this.popover.addEventListener("keydown", (ev) => {
      if (!this.open) return;
      if (ev.key === "ArrowDown") {
        ev.preventDefault();
        this.moveFocus(1);
      } else if (ev.key === "ArrowUp") {
        ev.preventDefault();
        this.moveFocus(-1);
      } else if (ev.key === "Home") {
        ev.preventDefault();
        this.focusedIndex = this.firstEnabledIndex();
        this.updateFocusVisual();
      } else if (ev.key === "End") {
        ev.preventDefault();
        for (let i = this.opts.length - 1; i >= 0; i--) {
          if (!this.opts[i]!.disabled) {
            this.focusedIndex = i;
            break;
          }
        }
        this.updateFocusVisual();
      } else if (ev.key === "Enter" || ev.key === " ") {
        ev.preventDefault();
        if (this.focusedIndex >= 0) {
          const o = this.opts[this.focusedIndex]!;
          if (!o.disabled) this.select(o.value);
        }
      } else if (ev.key === "Escape") {
        ev.preventDefault();
        this.setOpen(false);
        this.trigger.focus();
      } else if (ev.key === "Tab") {
        // Let Tab leave the dropdown; close on the way out so focus
        // doesn't return to a stale popover state.
        this.setOpen(false);
      }
    });

    // When the popover opens, route keyboard nav by moving DOM focus into
    // it. We listen for the open transition by hooking the trigger click
    // (handled above) and explicitly focusing on next tick.
    this.trigger.addEventListener("click", () => {
      if (this.open) {
        queueMicrotask(() => this.popover.focus());
      }
    });
  }

  private moveFocus(delta: number): void {
    if (this.itemEls.length === 0) return;
    let i = this.focusedIndex;
    for (let n = 0; n < this.itemEls.length; n++) {
      i = (i + delta + this.itemEls.length) % this.itemEls.length;
      if (!this.opts[i]!.disabled) {
        this.focusedIndex = i;
        this.updateFocusVisual();
        this.itemEls[i]?.scrollIntoView({ block: "nearest" });
        return;
      }
    }
  }

  // ----- chevron icon -----

  private drawChevron(): void {
    const dpr = window.devicePixelRatio || 1;
    const size = 12;
    if (this.chevron.width !== size * dpr) {
      this.chevron.width = size * dpr;
      this.chevron.height = size * dpr;
    }
    const ctx = this.chevron.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size, size);
    // Paint in a neutral dim grey. The chevron reads as a quiet icon and
    // doesn't need to track the trigger's hover colour shift — that gets
    // distracting when only the icon flickers between states.
    ctx.strokeStyle = "rgba(124, 135, 148, 0.85)";
    ctx.lineWidth = 1.6;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(2.5, 4.5);
    ctx.lineTo(6, 8);
    ctx.lineTo(9.5, 4.5);
    ctx.stroke();
  }
}
